import type { TFunction } from 'i18next';
import { Color } from '@draughts/shared';
import type { GameEndReason } from '@draughts/core';
import type { LocalEndAction } from '../store/game-store';

/**
 * Perché la partita è finita, in parole.
 *
 * Un solo posto per tutte le schermate di fine partita — locale, online, arena/torneo e
 * spettatore — perché prima ognuna aveva il suo `switch`: la partita locale non spiegava
 * niente (diceva "il Nero non ha più mosse" anche dopo un abbandono) e quella online
 * spiegava solo nella riga del turno, non nel riquadro di fine partita.
 *
 * Due vocabolari confluiscono qui:
 *  - il MOTORE (`GameEndReason` da @draughts/core) per le fini che si leggono dalla
 *    posizione: senza mosse, senza pezzi, prima dama, limite di mosse, ripetizione,
 *    aggiudicazioni di variante. Porta già con sé il lato a cui si riferisce;
 *  - le AZIONI, che la posizione non racconta: abbandono, patta accordata, tempo scaduto,
 *    disconnessione. In locale arrivano come `LocalEndAction`, dal server come stringa
 *    nuda (`resignation`, `no_moves`, `fifty_moves`, …) e senza lato, che si ricava dal
 *    risultato.
 *
 * Il motivo dice sempre il FATTO, mai chi ha vinto: il titolo del risultato lo dice già.
 * È ciò che tiene la frase vera anche in antidama, dove restare senza mosse È la vittoria.
 */

export type EndReasonInput = GameEndReason | LocalEndAction | string | null | undefined;

export interface EndReasonContext {
  /** Chi ha perso la partita. Serve ai motivi che il server manda senza lato. */
  loser?: Color;
  /** Modalità di gioco: in antidama chi resta senza mosse è il vincitore, non il perdente. */
  ruleMode?: string;
}

/** Nome tradotto del lato. */
function sideName(t: TFunction, side: Color | undefined): string | null {
  if (side === Color.WHITE) return t('game.clock.white');
  if (side === Color.BLACK) return t('game.clock.black');
  return null;
}

const other = (side: Color): Color => (side === Color.WHITE ? Color.BLACK : Color.WHITE);

/** "Il bianco non ha più mosse legali." — chiavi già tradotte in tutte le lingue. */
function noMovesText(t: TFunction, stuck: Color | undefined): string {
  if (stuck === Color.WHITE) return t('game.endReason.whiteNoMoves');
  if (stuck === Color.BLACK) return t('game.endReason.blackNoMoves');
  return t('game.endReason.noLegalMoves');
}

/**
 * Motivi che il SERVER manda come stringa nuda (`game:end`), senza lato: lo si ricava
 * dal risultato, così anche l'online ottiene la stessa frase completa del locale.
 */
function serverReasonText(t: TFunction, reason: string, ctx: EndReasonContext): string {
  const loser = ctx.loser;
  const loserName = sideName(t, loser);

  switch (reason) {
    case 'resignation':
      return loserName ? t('game.endReason.sideResigned', { side: loserName }) : t('game.endReason.resignation');
    case 'timeout':
      return loserName ? t('game.endReason.sideTimeout', { side: loserName }) : t('game.endReason.timeout');
    case 'agreement':
      return t('game.endReason.drawAgreed');
    case 'no_moves':
      // Il server manda 'no_moves' anche per le modalità non standard: in breakthrough
      // la partita è invece finita sulla promozione, e in antidama il lato bloccato è
      // quello che ha VINTO.
      if (ctx.ruleMode === 'breakthrough') return t('game.endReason.firstKing');
      if (loser === undefined) return t('game.endReason.noLegalMoves');
      return noMovesText(t, ctx.ruleMode === 'antidraughts' ? other(loser) : loser);
    case 'fifty_moves':
      return t('game.endReason.moveLimit');
    case 'repetition':
      return t('game.endReason.repetition');
    case 'endgame_draw':
      return t('game.endReason.endgameDraw');
    case 'disconnection':
    case 'opponent_abandoned':
      return t('game.endReason.disconnection');
    case 'walkover':
      return t('game.endReason.walkover');
    case 'aborted':
    case 'aborted_before_start':
      return t('game.endReason.gameAbandoned');
    case 'no_first_move':
      // La prima mossa non è arrivata entro la scadenza. In arena è una
      // sconfitta a tavolino e il perdente c'è; fuori la partita è annullata e
      // il lato non lo si conosce, quindi la frase resta impersonale.
      return loserName
        ? t('game.endReason.sideNoFirstMove', { side: loserName })
        : t('game.endReason.noFirstMove');
    // Un motivo sconosciuto è comunque meglio del nulla, ma non lo si mostra grezzo:
    // sarebbe uno slug inglese in mezzo a una frase tradotta.
    default:
      return t('game.endReason.gameEnded');
  }
}

export function endReasonText(t: TFunction, reason: EndReasonInput, ctx: EndReasonContext = {}): string {
  if (!reason) return '';
  if (typeof reason === 'string') return serverReasonText(t, reason, ctx);

  const side = sideName(t, reason.side);
  switch (reason.kind) {
    case 'resignation':
      return side ? t('game.endReason.sideResigned', { side }) : t('game.endReason.resignation');
    case 'agreement':
      return t('game.endReason.drawAgreed');
    case 'timeout':
      return side ? t('game.endReason.sideTimeout', { side }) : t('game.endReason.timeout');
    /*
     * La partita in presenza chiusa dai due giocatori. Dice un'altra cosa dallo
     * STESSO nome quando arriva dal server come stringa nuda (sopra, in
     * `serverReasonText`: «la partita e' stata abbandonata»), e non e' una
     * dimenticanza — sono due fatti diversi. La' una partita online annullata,
     * qui due persone a un tavolo che smettono: l'unica cosa da dire e' che non
     * c'e' un vincitore.
     */
    case 'aborted':
      return t('game.endReason.noWinner');
    case 'no_moves':
      return noMovesText(t, reason.side);
    case 'no_pieces':
      return side ? t('game.endReason.sideNoPieces', { side }) : t('game.endReason.gameEnded');
    case 'first_king':
      return t('game.endReason.firstKing');
    case 'move_limit':
      return t('game.endReason.moveLimit');
    case 'repetition':
      return t('game.endReason.repetition');
    case 'endgame_draw':
      return t('game.endReason.endgameDraw');
    case 'zugzwang':
      return side ? t('game.endReason.sideZugzwang', { side }) : t('game.endReason.gameEnded');
    case 'lone_king':
      return t('game.endReason.loneKing');
    case 'single_piece':
      return side ? t('game.endReason.sideSinglePiece', { side }) : t('game.endReason.gameEnded');
    default:
      return t('game.endReason.gameEnded');
  }
}
